if (!customElements.get("advanced-slider")) {
  class AdvancedSlider extends HTMLElement {

    connectedCallback() {
      this._index        = 0;
      this._total        = 0;
      this._startX       = 0;
      this._isDragging   = false;
      this._slidesPerView = 1;

      this._container = this.querySelector(".advanced-slider__continer");
      this._slides    = Array.from(this.querySelectorAll(".advanced-slider__slide"));
      this._dotsWrap  = this.querySelector(".advanced-slider__dots");
      this._prevBtn   = this.querySelector(".advanced-slider__arrows_left");
      this._nextBtn   = this.querySelector(".advanced-slider__arrows_right");

      if (this._slides.length === 0) return;

      this._init();
      this._bindEvents();
    }

    disconnectedCallback() {
      this._prevBtn?.removeEventListener("click", this._onPrev);
      this._nextBtn?.removeEventListener("click", this._onNext);
      window.removeEventListener("resize", this._onResize);
      window.removeEventListener("mouseup", this._onMouseUp);
    }

    _init() {
      this._setSlidesPerView();
      this._total = Math.ceil(this._slides.length / this._slidesPerView);
      if (this._index >= this._total) this._index = this._total - 1;

      if (this._slides.length === 1) {
        this.classList.add("advanced-slider--single");
      } else {
        this.classList.remove("advanced-slider--single");
      }

      this._buildDots();
      this._updateUI();
      this.classList.add("is-ready");
    }

    _setSlidesPerView() {
      const breakpoint = parseInt(this.dataset.breakpoint) || 768;
      const isMobile   = window.innerWidth <= breakpoint;

      const desktop = parseInt(this.dataset.slidesPerView)       || 1;
      const mobile  = parseInt(this.dataset.slidesPerViewMobile) || 1;

      this._slidesPerView = isMobile ? mobile : desktop;

      this.style.setProperty("--slides-per-view", this._slidesPerView);
    }

    _buildDots() {
      if (!this._dotsWrap) return;
      this._dotsWrap.innerHTML = "";

      for (let i = 0; i < this._total; i++) {
        const dot = document.createElement("button");
        dot.className = "advanced-slider__dot" + (i === 0 ? " active" : "");
        dot.setAttribute("data-index", (i));
        dot.setAttribute("aria-label", "Go to page " + (i + 1));
        dot.addEventListener("click", () => this.goTo(i));
        this._dotsWrap.appendChild(dot);
      }
    }

    _updateUI() {
      if (this._slides.length === 1) {
        this._container.style.transform = "none";
        if (this._prevBtn) this._prevBtn.closest(".advanced-slider__arrows").style.display = "none";
        if (this._dotsWrap) this._dotsWrap.style.display = "none";
        return;
      }

      const firstSlideOfPage = this._index * this._slidesPerView;
      const targetSlide      = this._slides[firstSlideOfPage];

      const offset = targetSlide ? targetSlide.offsetLeft : 0;
      this._container.style.transform = `translateX(-${offset}px)`;

      this._dotsWrap?.querySelectorAll(".advanced-slider__dot").forEach((d, i) => {
        d.classList.toggle("active", i === this._index);
      });

      this._prevBtn?.classList.toggle("disabled", this._index === 0);
      this._nextBtn?.classList.toggle("disabled", this._index === this._total - 1);

      const hideControls = this._total <= 1;
      if (this._prevBtn) this._prevBtn.closest(".advanced-slider__arrows").style.display = hideControls ? "none" : "";
      if (this._dotsWrap) this._dotsWrap.style.display = hideControls ? "none" : "";

      this.dispatchEvent(new CustomEvent("AdvancedSlideChange", {
        bubbles: true,
        detail: { this_el: this, index: this._index, total: this._total, slidesPerView: this._slidesPerView }
      }));
    }

    goTo(index) {
      if (index < 0 || index >= this._total) return;
      this._index = index;
      this._updateUI();
    }

    next() { this.goTo(this._index + 1); }
    prev() { this.goTo(this._index - 1); }

    _bindEvents() {
      this._onPrev = () => this.prev();
      this._onNext = () => this.next();
      this._prevBtn?.addEventListener("click", this._onPrev);
      this._nextBtn?.addEventListener("click", this._onNext);

      this._container.addEventListener("touchstart", e => {
        this._startX = e.touches[0].clientX;
      }, { passive: true });

      this._container.addEventListener("touchend", e => {
        const delta = this._startX - e.changedTouches[0].clientX;
        if (Math.abs(delta) > 40) delta > 0 ? this.next() : this.prev();
      }, { passive: true });

      this._container.addEventListener("mousedown", e => {
        this._startX    = e.clientX;
        this._isDragging = true;
        this._container.style.cursor = "grabbing";
        e.preventDefault();
      });

      this._onMouseUp = e => {
        if (!this._isDragging) return;
        const delta = this._startX - e.clientX;
        if (Math.abs(delta) > 40) delta > 0 ? this.next() : this.prev();
        this._isDragging = false;
        this._container.style.cursor = "";
      };
      window.addEventListener("mouseup", this._onMouseUp);

      this.setAttribute("tabindex", "0");
      this.addEventListener("keydown", e => {
        if (e.key === "ArrowRight") this.next();
        if (e.key === "ArrowLeft")  this.prev();
      });

      let lastBreakpoint = window.innerWidth <= (parseInt(this.dataset.breakpoint) || 768);
      this._onResize = this._debounce(() => {
        const nowMobile = window.innerWidth <= (parseInt(this.dataset.breakpoint) || 768);
        if (nowMobile !== lastBreakpoint) {
          lastBreakpoint = nowMobile;
          this._index = 0;
          this._init();
        } else {
          this._updateUI();
        }
      }, 200);
      window.addEventListener("resize", this._onResize);
    }

    _debounce(fn, ms) {
      let t;
      return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
    }
  }
  customElements.define("advanced-slider", AdvancedSlider);
}